import React from "react";
import { Badge, Button, Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router";

type Variant = string;

interface IToolbarProps {
  children: React.ReactNode;
  className?: string;
}

/** Row of page actions. Keep it to a couple of buttons plus menus. */
export const PageToolbar: React.FC<IToolbarProps> = ({
  children,
  className,
}) => (
  <div className={`d-flex flex-wrap align-items-center gap-2 mb-3 ${className ?? ""}`}>
    {children}
  </div>
);

interface IToolbarButtonProps {
  label: string;
  /** Required: every action says what it does, so nobody has to guess. */
  hint: string;
  icon?: string;
  variant?: Variant;
  to?: string;
  onClick?: () => void;
  disabled?: boolean;
  count?: number;
  type?: "button" | "submit";
}

export const ToolbarButton: React.FC<IToolbarButtonProps> = ({
  label,
  hint,
  icon,
  variant = "outline-secondary",
  to,
  onClick,
  disabled,
  count,
  type = "button",
}) => {
  const button = (
    <Button
      {...(to ? { as: Link as any, to } : {})}
      type={to ? undefined : type}
      variant={variant}
      size="sm"
      onClick={onClick}
      disabled={disabled}
      // A disabled button swallows pointer events, so the tooltip wrapper takes them.
      style={disabled ? { pointerEvents: "none" } : undefined}
    >
      {icon && <i className={`bi ${icon} me-1`} />}
      {label}
      {typeof count === "number" && count > 0 && (
        <Badge bg="light" text="dark" className="ms-2">
          {count}
        </Badge>
      )}
    </Button>
  );

  return (
    <OverlayTrigger placement="bottom" overlay={<Tooltip>{hint}</Tooltip>}>
      <span className="d-inline-block">{button}</span>
    </OverlayTrigger>
  );
};

interface IToolbarMenuProps {
  label: string;
  icon?: string;
  variant?: Variant;
  children: React.ReactNode;
  /** Shown next to the label, e.g. the number of selected rows the menu acts on. */
  count?: number;
  disabled?: boolean;
}

export const ToolbarMenu: React.FC<IToolbarMenuProps> = ({
  label,
  icon,
  variant = "outline-secondary",
  children,
  count,
  disabled,
}) => (
  <Dropdown>
    <Dropdown.Toggle variant={variant} size="sm" disabled={disabled}>
      {icon && <i className={`bi ${icon} me-1`} />}
      {label}
      {typeof count === "number" && count > 0 && (
        <Badge bg="light" text="dark" className="ms-2">
          {count}
        </Badge>
      )}
    </Dropdown.Toggle>
    <Dropdown.Menu style={{ minWidth: "20rem" }}>{children}</Dropdown.Menu>
  </Dropdown>
);

interface IToolbarMenuItemProps {
  label: string;
  /** The explainer, printed under the label rather than hidden in a tooltip. */
  hint: string;
  icon?: string;
  to?: string;
  onClick?: () => void;
  disabled?: boolean;
  count?: number;
}

export const ToolbarMenuItem: React.FC<IToolbarMenuItemProps> = ({
  label,
  hint,
  icon,
  to,
  onClick,
  disabled,
  count,
}) => (
  <Dropdown.Item
    {...(to ? { as: Link as any, to } : {})}
    onClick={onClick}
    disabled={disabled}
    className="py-2"
  >
    <div className="d-flex align-items-center">
      {icon && <i className={`bi ${icon} me-2`} />}
      <span className="fw-semibold">{label}</span>
      {typeof count === "number" && count > 0 && (
        <Badge bg="secondary" className="ms-2">
          {count}
        </Badge>
      )}
    </div>
    <div
      className="text-muted small text-wrap"
      style={{ whiteSpace: "normal", lineHeight: 1.25 }}
    >
      {hint}
    </div>
  </Dropdown.Item>
);

export const ToolbarMenuHeader: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <Dropdown.Header>{children}</Dropdown.Header>;

export const ToolbarDivider: React.FC = () => (
  <div className="vr mx-1 d-none d-lg-block" style={{ height: "1.75rem" }} />
);
